import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Stack,
  Image,
  Text,
  Grid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Icon,
  Button,
  Spinner,
  useColorModeValue,
  Badge,
  Flex,
  Divider,
  HStack,
  ModalFooter,
  Tag,
  Tooltip,
  useColorMode,
} from "@chakra-ui/react";
import { FaRocket } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { chakra } from "@chakra-ui/react";
import { collection, getDocs } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/swiper-bundle.css";
import PromptGallery from "./PromptGallery";
import LottieLoader from "../Animations/LottieLoader";
import FeaturedProjectCard from "./FeaturedProjectCard";
import { getIconComponent } from "./iconUtils";
import { usePortfolioData } from "../PortfolioDataProvider";
import StackedCards from "./StackedCards";

const MotionBox = chakra(motion.div);

// A little helper to preload all images before revealing
const useImagesPreloaded = (imageUrls = []) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let canceled = false;
    if (!imageUrls || imageUrls.length === 0) {
      setLoaded(true);
      return;
    }
    let loadedCount = 0;
    imageUrls.forEach((url) => {
      const img = new window.Image();
      img.src = url;
      img.onload = img.onerror = () => {
        loadedCount++;
        if (loadedCount === imageUrls.length && !canceled) {
          setLoaded(true);
        }
      };
    });
    return () => {
      canceled = true;
    };
  }, [imageUrls]);

  return loaded;
};

const ProjectModal = ({
  isOpen,
  onClose,
  project,
  currentIndex,
  setCurrentIndex,
}) => {
  const { colorMode } = useColorMode();
  const isFeatured = project?.featured;
  const isN8n = project?.category === "n8n" || project?.n8n;

  // n8n entries store images under `images` instead of `modalImages`
  const modalImages = project?.modalImages?.length
    ? project.modalImages
    : project?.images || [];

  // Dark/light bg
  const glassBg =
    colorMode === "dark" ? "rgba(15,17,27,0.96)" : "rgba(243,245,250,0.98)";
  const cardShadow =
    colorMode === "dark"
      ? "0px 10px 40px 0px #161b2644"
      : "0px 8px 28px 0px #23293b33";
  const textPrimary = colorMode === "dark" ? "#F7F7F8" : "#181F29";
  const textSecondary = colorMode === "dark" ? "#b7bed6" : "#283041";
  const techPillBg = colorMode === "dark" ? "#293040" : "#f7f7fa";
  const apiPillBg = colorMode === "dark" ? "#353b4a" : "#e7eaf0";
  const badgeBg = colorMode === "dark" ? "#23293b" : "#FFD580";
  const badgeText = colorMode === "dark" ? "#FFD580" : "#23293b";

  const imagesLoaded = useImagesPreloaded(modalImages);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "full", md: "3xl" }}
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        bg={glassBg}
        boxShadow={cardShadow}
        borderRadius="2xl"
        maxH={{ base: "100vh", md: "80vh" }}
        p={isFeatured ? 2 : 0}
        position="relative"
      >
        {isFeatured && (
          <Badge
            pos="absolute"
            left={6}
            top={6}
            zIndex="10"
            px={5}
            py={2}
            fontSize="1em"
            borderRadius="full"
            bg={badgeBg}
            color={badgeText}
            fontWeight="bold"
          >
            Featured
          </Badge>
        )}
        <ModalHeader>
          <Heading
            size={{ base: "md", md: "xl" }}
            fontWeight="extrabold"
            color={textPrimary}
            mb={0}
          >
            {project.title}
          </Heading>
        </ModalHeader>
        <ModalCloseButton color={textPrimary} size="lg" />
        <ModalBody overflowY="auto" px={{ base: 2, md: 6 }} py={4}>
          <Text color={textSecondary} mb={3} fontSize="md" fontWeight={500}>
            {project.modalDescription}
          </Text>

          {/* Loader while images load */}
          {!imagesLoaded ? (
            <Flex align="center" justify="center" minH="260px">
              <LottieLoader />
            </Flex>
          ) : (
            <Box
              position="relative"
              width="100%"
              height={{ base: "260px", md: "340px" }}
              mb={6}
              zIndex={2}
              borderRadius="xl"
              overflow="hidden"
              bg={colorMode === "dark" ? "#1a2236" : "#f4f6fa"}
            >
              <Swiper
                modules={[Navigation, Pagination]}
                navigation
                pagination={{ clickable: true }}
                spaceBetween={40}
                onSlideChange={(swiper) => setCurrentIndex(swiper.activeIndex)}
                initialSlide={currentIndex}
                style={{ height: "100%" }}
                loop
              >
                {modalImages.map((image, idx) => (
                  <SwiperSlide key={idx}>
                    <Box
                      as="img"
                      src={image}
                      alt={`${project.title} - ${idx + 1}`}
                      objectFit="contain"
                      width="100%"
                      height="100%"
                      borderRadius="xl"
                      bg="rgba(0,0,0,0.04)"
                      userSelect="none"
                      draggable={false}
                      loading="lazy"
                      style={{ display: "block" }}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>
          )}

          {/* Tools/Technologies */}
          {!isN8n && project?.techStack?.length > 0 && (
            <Box mt={imagesLoaded ? 4 : 0}>
              <Text fontWeight="bold" mb={2} color={textPrimary} fontSize="lg">
                Tools/Technologies Used:
              </Text>
              <HStack wrap="wrap" spacing={4}>
                {project.techStack.map((tech, index) => (
                  <Tag
                    key={index}
                    display="flex"
                    alignItems="center"
                    px={4}
                    py={2}
                    borderRadius="full"
                    bg={techPillBg}
                    color={textPrimary}
                    fontWeight="bold"
                    fontSize="sm"
                    gap={2}
                  >
                    {getIconComponent(tech.icon)}
                    <span>{tech.title}</span>
                  </Tag>
                ))}
              </HStack>
            </Box>
          )}

          {/* External APIs */}
          {!isN8n &&
            isFeatured &&
            project.externalAPIs &&
            project.externalAPIs.length > 0 && (
              <Box pt={2} w="full">
                <Text
                  fontWeight="bold"
                  fontSize="md"
                  color={textPrimary}
                  pb={1}
                >
                  External APIs
                </Text>
                <HStack flexWrap="wrap" gap={3} w="full">
                  {project.externalAPIs.map((api, idx) => (
                    <Tooltip label={api.description} fontSize="sm" key={idx}>
                      <Tag
                        px={4}
                        py={2}
                        borderRadius="full"
                        bg={apiPillBg}
                        color={textPrimary}
                        fontWeight="bold"
                        fontSize="sm"
                        gap={2}
                        _hover={{
                          filter: "brightness(1.10)",
                          boxShadow: "0 0 10px #23293b44",
                          transform: "scale(1.08)",
                        }}
                        transition="all .2s cubic-bezier(.6,.3,.25,1)"
                      >
                        {getIconComponent(api.icon)}
                        <span>{api.title}</span>
                      </Tag>
                    </Tooltip>
                  ))}
                </HStack>
              </Box>
            )}
        </ModalBody>
        <ModalFooter
          display="flex"
          justifyContent="flex-end"
          bg="transparent"
          borderBottomRadius="2xl"
        >
          <Button
            colorScheme={isFeatured ? "yellow" : "blue"}
            variant={isFeatured ? "solid" : "ghost"}
            size="lg"
            borderRadius="full"
            px={8}
            fontWeight="bold"
            shadow="md"
            onClick={onClose}
          >
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const PortfolioCard = ({ project, onClick }) => {
  // Define color mode based styles
  const cardBg = useColorModeValue("white", "gray.800");
  const cardShadow = useColorModeValue(
    "0px 4px 20px rgba(0, 0, 0, 0.1)",
    "0px 4px 20px rgba(0, 0, 0, 0.6)"
  );
  const badgeBg = useColorModeValue(
    "linear-gradient(90deg, rgba(34,193,195,1) 0%, rgba(253,187,45,1) 100%)",
    "linear-gradient(90deg, rgba(34,193,195,1) 0%, rgba(253,45,45,1) 100%)"
  );
  const textColor = useColorModeValue("gray.800", "whiteAlpha.900");

  return (
    <MotionBox
      onClick={onClick}
      cursor="pointer"
      //whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      bg={cardBg}
      borderRadius="xl"
      overflow="hidden"
      boxShadow={cardShadow}
      p={6}
      position="relative"
      transition="box-shadow 0.3s ease"
    >
      <Box borderRadius="xl" overflow="hidden" mb={4}>
        <Image
          src={project.coverImage}
          alt={project.title}
          objectFit="cover"
          w={{ base: "100%", md: "100%" }} // Full width for mobile devices
          h={{ base: "200px", md: "250px" }} // Adjust the height for mobile devices
          borderRadius="lg"
        />
      </Box>
      <Heading
        as="h3"
        size={{ base: "md", md: "lg" }} // Smaller size for mobile devices
        fontWeight="extrabold"
        mb={2}
        color={textColor}
      >
        {project.title}
      </Heading>
      <Text fontSize="sm" color={textColor} mb={4}>
        {project.description}
      </Text>
      <Badge
        bg={badgeBg}
        color="white"
        borderRadius="full"
        px={3}
        py={1}
        fontSize="0.8em"
        textTransform="capitalize"
      >
        {project.category}
      </Badge>
    </MotionBox>
  );
};

const PortfolioSection = ({ headTitle, description, projects }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedProject, setSelectedProject] = useState(null);
  const textColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const color = useColorModeValue("gray.300", "gray.600");

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setCurrentIndex(0); // Reset the carousel to the first image
    onOpen();
  };

  return (
    <Box mb={10}>
      <Flex justifyContent="space-between" alignItems="center" mb={8}>
        <Box>
          <Heading
            as="h1"
            size={{ base: "md", md: "xl" }}
            color={textColor}
            mb={2}
          >
            {headTitle}
          </Heading>
          <Text fontSize={{ base: "sm", md: "md" }} color={textColor}>
            {description}
          </Text>
          <Divider my={"4"} color={color} />
        </Box>
      </Flex>
      <Grid
        templateColumns={{
          base: "repeat(auto-fit, minmax(200px, 1fr))",
          md: "repeat(auto-fit, minmax(400px, 1fr))",
        }}
        gap={8}
      >
        {projects.map((project, index) =>
          project.featured ? (
            <FeaturedProjectCard
              key={index}
              project={project}
              onClick={() => handleProjectClick(project)}
            />
          ) : (
            <PortfolioCard
              key={index}
              project={project}
              onClick={() => handleProjectClick(project)}
            />
          )
        )}
      </Grid>
      {selectedProject && (
        <ProjectModal
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          isOpen={isOpen}
          onClose={onClose}
          project={selectedProject}
        />
      )}
    </Box>
  );
};

const Portfolio = () => {
  //const [loading, setLoading] = useState(true);
  const { projectsData, sideProjectsData, n8nProjects, loading } = usePortfolioData();
  //const [sideProjectsData, setSideProjectsData] = useState(null);
  const [images, setImages] = useState([]);
  const [imagesForEachCategory, setImagesForEachCategory] = useState([]);

  const bg = useColorModeValue("white", "gray.800");

  const spinnerColor = useColorModeValue("gray.800", "gray.200");
  const spinnerEmptyColor = useColorModeValue("gray.300", "gray.700");

  if (loading) {
    return (
      <Box
        position={{ base: "fixed", md: "absolute" }}
        top="0"
        left="0"
        right="0"
        bottom="0"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg={bg} // Optional: Add a background to match the Portfolio section's bg
      >
        <LottieLoader />
      </Box>
    );
  }

  return (
    <Stack spacing={12} p={8} bg={bg}>
      {/* <StackedCards 
        projects={projectsData}           // or projectsData.filter(p => p.featured)
        maxCards={5}
      /> */}
      <PortfolioSection
        headTitle={"Featured"}
        description={
          "Showcasing innovative solutions and standout builds that highlight my passion for technology, creativity, and real-world impact."
        }
        projects={projectsData.filter((project) => project.featured)}
      />
      <PortfolioSection
        headTitle={"AI Automation workflows"}
        description={
          "These projects represent my work in building AI Agents and Workflow development, automating most manual tasks using n8n and other automation tools to solve real-world problems."
        }
        projects={n8nProjects}
      />
      <PortfolioSection
        headTitle={"Projects"}
        description={
          "These projects represent my work in cross-platform development and UI/UX design, including full-stack applications and systems built to solve real-world problems."
        }
        projects={projectsData.filter(
          (project) =>
            !project.featured &&
            project?.category !== "n8n" &&
            project?.n8n !== true
        )}
      />
      <PortfolioSection
        headTitle={"Other projects/Side projects"}
        description={
          "A collection of side projects where I explored new technologies and worked on smaller-scale applications to enhance my skills in development and design."
        }
        projects={sideProjectsData}
      />
      <PromptGallery />
    </Stack>
  );
};

export default Portfolio;
